import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Users, ClipboardList, Clock, AlertTriangle, CheckCircle2, CalendarDays, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const COACHES = ['Paul Brailsford', 'Peter Wright'];

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [upcomingAssessments, setUpcomingAssessments] = useState([]);
  const [overdueAssessments, setOverdueAssessments] = useState([]);
  const [allAssessments, setAllAssessments] = useState([]);
  const [driversWithAccidents, setDriversWithAccidents] = useState([]);
  const [accidents24h, setAccidents24h] = useState([]);
  const [accidentsWeek, setAccidentsWeek] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpcomingDialog, setShowUpcomingDialog] = useState(false);
  const [showOverdueDialog, setShowOverdueDialog] = useState(false);
  const [showAccidentsDialog, setShowAccidentsDialog] = useState(false);
  const [show24hDialog, setShow24hDialog] = useState(false);
  const [showWeekDialog, setShowWeekDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [coachStatus, setCoachStatus] = useState(null);

  useEffect(() => {
    fetchCurrentUser();
    fetchDashboardData();
  }, []);

  useEffect(() => {
    fetchCoachTodayStatus();
  }, [currentUser]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, getAuthHeaders());
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user');
    }
  };

  const fetchCoachTodayStatus = async () => {
    if (!currentUser?.username || !COACHES.includes(currentUser.username)) {
      setCoachStatus(null);
      return;
    }

    try {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date();
      end.setHours(23, 59, 59, 999);

      const res = await axios.get(
        `${API}/coach-schedule?start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`,
        getAuthHeaders()
      );

      const items = (res.data || []).filter((it) => (it.coaches || []).includes(currentUser.username));

      const manual = items.filter((it) => it.source === 'manual');
      if (manual.length > 0) {
        // pick earliest manual event for today as "status"
        manual.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
        setCoachStatus({
          type: manual[0].event_type,
          title: manual[0].title,
          start: manual[0].start_datetime,
          end: manual[0].end_datetime,
        });
      } else {
        setCoachStatus(null);
      }
    } catch {
      setCoachStatus(null);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [statsRes, upcomingRes, overdueRes, allAssessmentsRes, driversRes, accidentsRes, accidents24hRes, accidentsWeekRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, getAuthHeaders()),
        axios.get(`${API}/dashboard/upcoming?limit=100`, getAuthHeaders()),
        axios.get(`${API}/dashboard/overdue`, getAuthHeaders()),
        axios.get(`${API}/assessments`, getAuthHeaders()),
        axios.get(`${API}/drivers`, getAuthHeaders()),
        axios.get(`${API}/dashboard/accidents`, getAuthHeaders()),
        axios.get(`${API}/dashboard/accidents-24h`, getAuthHeaders()),
        axios.get(`${API}/dashboard/accidents-week`, getAuthHeaders()),
      ]);
      
      // Enrich assessments with driver names
      const assessmentsWithDrivers = allAssessmentsRes.data.map(assessment => {
        const driver = driversRes.data.find(d => d.id === assessment.driver_id);
        return {
          ...assessment,
          driver_name: driver ? driver.name : 'Unknown'
        };
      });
      
      setStats(statsRes.data);
      setUpcomingAssessments(upcomingRes.data);
      setOverdueAssessments(overdueRes.data);
      setAllAssessments(assessmentsWithDrivers);
      setDriversWithAccidents(accidentsRes.data);
      setAccidents24h(accidents24hRes.data);
      setAccidentsWeek(accidentsWeekRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading dashboard...</div>;
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <h1 className="text-4xl font-bold text-slate-800 mb-2">Dashboard</h1>
        <p className="text-slate-600">Overview of driver assessments, training, schedules and RTCs</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Card 
          data-testid="stat-card-drivers" 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/drivers')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Drivers</CardTitle>
            <Users className="h-5 w-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-800">{stats?.total_drivers || 0}</div>
            <p className="text-xs text-slate-500 mt-1">Click to view all drivers</p>
          </CardContent>
        </Card>

        <Card 
          data-testid="stat-card-assessments"
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/assessments')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Assessments</CardTitle>
            <ClipboardList className="h-5 w-5 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-800">{stats?.total_assessments || 0}</div>
            <p className="text-xs text-slate-500 mt-1">Click to view all assessments</p>
          </CardContent>
        </Card>

        <Card data-testid="stat-card-overdue" className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setShowOverdueDialog(true)}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Due P2P Reviews</CardTitle>
            <AlertTriangle className="h-5 w-5 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-800">{stats?.overdue_assessments || 0}</div>
            <p className="text-xs text-slate-500 mt-1">Click to view drivers</p>
          </CardContent>
        </Card>

        <Card data-testid="stat-card-accidents" className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setShowAccidentsDialog(true)}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total RTCs</CardTitle>
            <AlertCircle className="h-5 w-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-800">{stats?.total_accidents || 0}</div>
            <p className="text-xs text-slate-500 mt-1">{stats?.drivers_with_accidents || 0} drivers</p>
          </CardContent>
        </Card>

        <Card data-testid="stat-card-accidents-24h" className="cursor-pointer hover:shadow-lg transition-shadow border-red-200 dark:border-red-900 bg-red-50/30 dark:bg-red-950/30" onClick={() => setShow24hDialog(true)}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-300">Last 24 Hours</CardTitle>
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-700 dark:text-red-400">{stats?.accidents_24h || 0}</div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">New RTCs</p>
          </CardContent>
        </Card>

        <Card data-testid="stat-card-accidents-week" className="cursor-pointer hover:shadow-lg transition-shadow border-amber-200 dark:border-amber-900 bg-amber-50/30 dark:bg-amber-950/30" onClick={() => setShowWeekDialog(true)}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-300">Last 7 Days</CardTitle>
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-700 dark:text-amber-400">{stats?.accidents_week || 0}</div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Recent RTCs</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <Card data-testid="todays-date-card" className="border-blue-200 dark:border-blue-900 bg-gradient-to-br from-blue-50 to-slate-50 dark:from-blue-950 dark:to-slate-900">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300 uppercase tracking-wide">
                  {format(new Date(), 'EEEE')}
                </p>
                <p className="text-6xl font-bold text-blue-700 dark:text-blue-400 my-2">
                  {format(new Date(), 'd')}
                </p>
                <p className="text-lg font-medium text-slate-700 dark:text-slate-300">
                  {format(new Date(), 'MMMM yyyy')}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="todays-assessments-card" className="border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center text-blue-700">
                <CalendarDays className="h-5 w-5 mr-2" />
                Today’s Assessments & Training
              </CardTitle>
            </CardHeader>

              {coachStatus && (
                <div
                  className="p-3 rounded-lg border border-purple-200 bg-purple-50 dark:bg-purple-950/30 dark:border-purple-900 cursor-pointer hover:shadow-md transition-all"
                  onClick={() => navigate('/coach-scheduler')}
                  data-testid="coach-status-banner"
                >
                  <p className="text-xs font-medium text-purple-700 dark:text-purple-300"><span className="text-green-600">ADC</span> schedule</p>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-800 dark:text-slate-200">{coachStatus.title}</p>
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        {format(new Date(coachStatus.start), 'HH:mm')}–{format(new Date(coachStatus.end), 'HH:mm')}
                      </p>
                    </div>
                    <Badge variant="secondary" className="bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900 dark:text-purple-200">
                      {coachStatus.type}
                    </Badge>
                  </div>
                  <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">Click to open <span className="text-green-600">ADC</span> Scheduler →</p>
                </div>
              )}

            <CardContent>
              <div className="space-y-3">
                {(() => {
                  const today = new Date();
                  const todaysAssessments = allAssessments.filter((assessment) => {
                    const assessmentDate = new Date(assessment.scheduled_date);
                    return assessmentDate.toDateString() === today.toDateString();
                  });
                  
                  if (todaysAssessments.length === 0) {
                    return <p className="text-slate-500 text-sm">No assessments or training scheduled for today</p>;
                  }
                  
                  return todaysAssessments.map((assessment) => (
                    <div 
                      key={assessment.id} 
                      className="p-3 bg-blue-50 dark:bg-blue-950/50 rounded-lg border border-blue-200 dark:border-blue-800 cursor-pointer hover:shadow-md hover:bg-blue-100 dark:hover:bg-blue-900 transition-all" 
                      data-testid={`today-assessment-${assessment.id}`}
                      onClick={() => navigate(`/drivers/${assessment.driver_id}`)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-slate-800 dark:text-slate-200">{assessment.driver_name}</p>
                          <p className="text-sm text-slate-600 dark:text-slate-400">{assessment.assessment_type}</p>
                          {assessment.course_title && (
                            <p className="text-xs text-slate-500 dark:text-slate-500">{assessment.course_title}</p>
                          )}
                        </div>
                        <Badge 
                          variant={assessment.status === 'completed' ? 'default' : assessment.status === 'overdue' ? 'secondary' : 'outline'}
                          className={assessment.status === 'completed' ? 'bg-blue-600 dark:bg-blue-500' : ''}
                        >
                          {assessment.status}
                        </Badge>
                      </div>
                      {assessment.assessor && (
                        <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                          {assessment.assessment_type === 'Training Course' ? 'Trainer' : 
                           assessment.assessment_type === 'Coaching Intervention' ? 'Coach' :
                           assessment.assessment_type === 'Coaching Ride Along' ? 'Coach' : 'Assessor'}: {assessment.assessor}
                        </p>
                      )}
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Click to view driver →</p>
                    </div>
                  ));
                })()}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card data-testid="upcoming-card" className="border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center text-blue-700">
                <CheckCircle2 className="h-5 w-5 mr-2" />
                Upcoming (Next 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {upcomingAssessments.length === 0 ? (
                  <p className="text-slate-500 text-sm">No upcoming assessments</p>
                ) : (
                  <>
                    {upcomingAssessments.map((assessment) => (
                    <div 
                      key={assessment.id} 
                      className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/50 rounded-lg border border-blue-200 dark:border-blue-800 cursor-pointer hover:shadow-md hover:bg-blue-100 dark:hover:bg-blue-900 transition-all" 
                      data-testid={`upcoming-assessment-${assessment.id}`}
                      onClick={() => navigate(`/drivers/${assessment.driver_id}`)}
                    >
                      <div>
                        <p className="font-medium text-slate-800 dark:text-slate-200">{assessment.driver_name}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-400">{assessment.assessment_type}</p>
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Click to view driver →</p>
                      </div>
                      <Badge variant="outline" className="bg-white dark:bg-slate-800 border-blue-300 dark:border-blue-700" data-testid="upcoming-badge">
                        {format(new Date(assessment.scheduled_date), 'MMM d, yyyy HH:mm')}
                      </Badge>
                    </div>
                  ))}
                  {upcomingAssessments.length > 5 && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 text-center pt-2 border-t dark:border-slate-700">
                      Scroll for more ({upcomingAssessments.length} total)
                    </p>
                  )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Upcoming Assessments Dialog */}
      <Dialog open={showUpcomingDialog} onOpenChange={setShowUpcomingDialog}>
        <DialogContent className="max-w-2xl" data-testid="upcoming-dialog">
          <DialogHeader>
            <DialogTitle>Upcoming Assessments (Next 7 Days)</DialogTitle>
            <DialogDescription>
              All assessments scheduled within the next week
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {upcomingAssessments.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No upcoming assessments, training in the next 7 days</p>
            ) : (
              upcomingAssessments.map((assessment) => (
                <div key={assessment.id} className="p-4 border rounded-lg bg-blue-50 border-blue-200" data-testid={`dialog-upcoming-${assessment.id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-slate-800">{assessment.driver_name}</p>
                      <p className="text-sm text-slate-600">{assessment.assessment_type}</p>
                      {assessment.course_title && (
                        <p className="text-sm text-slate-500">{assessment.course_title}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="bg-white border-blue-300">
                      {format(new Date(assessment.scheduled_date), 'MMM d, yyyy HH:mm')}
                    </Badge>
                  </div>
                  {assessment.assessor && (
                    <p className="text-sm text-slate-600">Assessor: {assessment.assessor}</p>
                  )}
                  {assessment.notes && (
                    <p className="text-sm text-slate-600 mt-1">{assessment.notes}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Overdue Dialog */}
      <Dialog open={showOverdueDialog} onOpenChange={setShowOverdueDialog}>
        <DialogContent className="max-w-2xl" data-testid="overdue-dialog">
          <DialogHeader>
            <DialogTitle>Due Peer-to-Peer Reviews</DialogTitle>
            <DialogDescription>
              Drivers with peer-to-peer reviews due for completion
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {overdueAssessments.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No due peer-to-peer reviews</p>
            ) : (
              overdueAssessments.map((assessment) => (
                <div 
                  key={assessment.id} 
                  className={`p-4 border rounded-lg ${
                    assessment.status === 'overdue'
                      ? 'bg-red-50 border-red-200'
                      : 'bg-amber-50 border-amber-200'
                  }`}
                  data-testid={`dialog-overdue-${assessment.id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-slate-800">{assessment.driver_name}</p>
                      <p className="text-sm text-slate-600">Yearly Peer-to-Peer Review</p>
                    </div>
                    <Badge 
                      variant={assessment.status === 'overdue' ? 'destructive' : 'secondary'}
                      className={assessment.status === 'due' ? 'bg-amber-100 text-amber-800 border-amber-300' : ''}
                    >
                      {assessment.status}
                    </Badge>
                  </div>
                  {assessment.assessor && (
                    <p className="text-sm text-slate-600">Assessor: {assessment.assessor}</p>
                  )}
                  {assessment.notes && (
                    <p className="text-sm text-slate-600 mt-1">{assessment.notes}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Accidents Dialog */}
      <Dialog open={showAccidentsDialog} onOpenChange={setShowAccidentsDialog}>
        <DialogContent className="max-w-2xl" data-testid="accidents-dialog">
          <DialogHeader>
            <DialogTitle>Drivers with Accident Records</DialogTitle>
            <DialogDescription>
              All drivers who have road traffic collision records
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {driversWithAccidents.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No accident records</p>
            ) : (
              driversWithAccidents.map((driver) => (
                <div 
                  key={driver.driver_id} 
                  className="p-4 border rounded-lg bg-orange-50 border-orange-200 cursor-pointer hover:shadow-md hover:bg-orange-100 transition-all" 
                  data-testid={`dialog-driver-accidents-${driver.driver_id}`}
                  onClick={() => {
                    setShowAccidentsDialog(false);
                    navigate(`/drivers/${driver.driver_id}`);
                  }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-slate-800">{driver.driver_name}</p>
                      {driver.employee_id && (
                        <p className="text-sm text-slate-600">ID: {driver.employee_id}</p>
                      )}
                    </div>
                    <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                      {driver.accident_count} accident{driver.accident_count > 1 ? 's' : ''}
                    </Badge>
                  </div>
                  <div className="text-sm text-slate-600">
                    <p>Most recent: {format(new Date(driver.most_recent_accident.date), 'MMM d, yyyy')}</p>
                    <p className="text-xs text-slate-500">{driver.most_recent_accident.description}</p>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">Click to view driver details →</p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 24 Hour Accidents Dialog */}
      <Dialog open={show24hDialog} onOpenChange={setShow24hDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>RTCs - Last 24 Hours</DialogTitle>
            <DialogDescription>Accidents reported in the last 24 hours</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {accidents24h.length === 0 ? (
              <p className="text-center py-8 text-slate-500">No accidents in last 24 hours</p>
            ) : (
              accidents24h.map((acc) => (
                <div
                  key={acc.driver_id + acc.accident_date}
                  className="p-4 border rounded-lg bg-red-50 border-red-200 cursor-pointer hover:bg-red-100 transition-all"
                  onClick={() => {
                    setShow24hDialog(false);
                    navigate(`/drivers/${acc.driver_id}`);
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-slate-800">{acc.driver_name}</p>
                      {acc.employee_id && <p className="text-sm text-slate-600">ID: {acc.employee_id}</p>}
                      <p className="text-sm text-slate-600">{format(new Date(acc.accident_date), 'MMM d, yyyy HH:mm')}</p>
                    </div>
                    <Badge variant="destructive">{acc.severity}</Badge>
                  </div>
                  <p className="text-sm text-slate-600 mt-2">{acc.description?.substring(0, 100)}...</p>
                  <p className="text-xs text-blue-600 mt-2">Click to view driver →</p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Week Accidents Dialog */}
      <Dialog open={showWeekDialog} onOpenChange={setShowWeekDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>RTCs - Last 7 Days</DialogTitle>
            <DialogDescription>Accidents reported in the last week</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {accidentsWeek.length === 0 ? (
              <p className="text-center py-8 text-slate-500">No accidents in last 7 days</p>
            ) : (
              accidentsWeek.map((acc) => (
                <div
                  key={acc.driver_id + acc.accident_date}
                  className="p-4 border rounded-lg bg-amber-50 border-amber-200 cursor-pointer hover:bg-amber-100 transition-all"
                  onClick={() => {
                    setShowWeekDialog(false);
                    navigate(`/drivers/${acc.driver_id}`);
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-slate-800">{acc.driver_name}</p>
                      {acc.employee_id && <p className="text-sm text-slate-600">ID: {acc.employee_id}</p>}
                      <p className="text-sm text-slate-600">{format(new Date(acc.accident_date), 'MMM d, yyyy HH:mm')}</p>
                    </div>
                    <Badge variant="secondary">{acc.severity}</Badge>
                  </div>
                  <p className="text-sm text-slate-600 mt-2">{acc.description?.substring(0, 100)}...</p>
                  <p className="text-xs text-blue-600 mt-2">Click to view driver →</p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}